/**
 * Wishlist Page Content Script
 * Handles loading and rendering wishlist products on the wishlist page
 */


(function () {
  'use strict';


  /** @type {Window & { wishlistPageInitialized?: boolean; wishlistPageRetryCount?: number }} */
  const globalWin = window;

  /**
   * @param {Element | null} el
   * @returns {HTMLElement | null}
   */
  function elToHtml(el) {
    return el instanceof HTMLElement ? el : null;
  }

  const WISHLIST_API_BASE = '/apps/ayudra/api';
  const PRODUCT_LIST_KEYS = [
    'products',
    'wishlistProducts',
    'wishlist_products',
    'items',
    'productIds',
    'product_ids',
    'ids',
    'value',
    'metafield',
    'wishlist',
  ];
  /** @type {Map<string, string>} */
  const resolvedHandleCache = new Map();

  /**
   * Customer metafield values are often product IDs or GIDs, not product objects.
   * @param {unknown} value
   * @returns {string}
   */
  function extractNumericId(value) {
    if (value == null || value === '') return '';
    const raw = String(value).trim();
    const gid = raw.match(/gid:\/\/shopify\/Product\/(\d+)/);
    if (gid) return gid[1];
    return /^\d+$/.test(raw) ? raw : '';
  }

  /**
   * @param {unknown} value
   * @param {number} [depth]
   * @returns {unknown[] | null}
   */
  function coerceToList(value, depth = 0) {
    if (value == null || depth > 4) return null;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed || (trimmed[0] !== '[' && trimmed[0] !== '{')) return null;
      try {
        return coerceToList(JSON.parse(trimmed), depth + 1);
      } catch {
        return null;
      }
    }
    if (Array.isArray(value)) return value;
    if (typeof value === 'object') {
      const record = /** @type {Record<string, unknown>} */ (value);
      for (const key of PRODUCT_LIST_KEYS) {
        if (key in record) {
          const nested = coerceToList(record[key], depth + 1);
          if (nested && nested.length) return nested;
        }
      }
    }
    return null;
  }

  /**
   * @param {unknown} result
   * @returns {unknown[]}
   */
  function extractProductList(result) {
    const sources = [];
    if (result && typeof result === 'object') {
      const record = /** @type {Record<string, unknown>} */ (result);
      if (record.data && typeof record.data === 'object') sources.push(record.data);
      sources.push(record);
    }
    for (const source of sources) {
      const list = coerceToList(source);
      if (list && list.length) return list;
    }
    return [];
  }

  /**
   * @param {unknown} item
   * @returns {{ id: string, productId: string, handle: string } | null}
   */
  function normalizeWishlistItem(item) {
    if (typeof item === 'string' || typeof item === 'number') {
      const raw = String(item).trim();
      const productId = extractNumericId(raw);
      if (productId) return { id: productId, productId, handle: '' };
      if (/^[a-z0-9][a-z0-9-]*$/i.test(raw)) return { id: '', productId: '', handle: raw };
      return null;
    }
    if (!item || typeof item !== 'object') return null;
    const record = /** @type {Record<string, any>} */ (item);
    const nested = record.product && typeof record.product === 'object' ? record.product : null;
    const handle = String(
      record.handle || record.productHandle || record.product_handle || nested?.handle || ''
    ).trim();
    const productId = extractNumericId(
      record.productId ?? record.product_id ?? record.id ?? record.gid ?? nested?.id ?? ''
    );
    if (!handle && !productId) return null;
    return {
      id: productId || String(record.id || ''),
      productId,
      handle,
    };
  }

  /**
   * Saved wishlist from the customer metafield, when the definition is readable on the storefront.
   * @returns {unknown[]}
   */
  function readSavedMetafieldProducts() {
    const node = document.getElementById('wishlist-saved-products');
    if (!node) return [];
    const text = (node.textContent || '').trim();
    if (!text || text === 'null' || text === '[]') return [];
    try {
      const list = coerceToList(JSON.parse(text));
      return list || [];
    } catch {
      return [];
    }
  }

  /**
   * @param {string} html
   * @returns {Array<{ id?: number | string, handle?: string }>}
   */
  function parseSearchMeta(html) {
    const marker = 'var meta = ';
    const start = html.indexOf(marker);
    if (start === -1) return [];
    const jsonStart = start + marker.length;
    if (html[jsonStart] !== '{') return [];
    let depth = 0;
    let inString = false;
    let escaped = false;
    for (let i = jsonStart; i < html.length; i++) {
      const char = html[i];
      if (inString) {
        if (escaped) escaped = false;
        else if (char === '\\') escaped = true;
        else if (char === '"') inString = false;
        continue;
      }
      if (char === '"') {
        inString = true;
      } else if (char === '{') {
        depth++;
      } else if (char === '}') {
        depth--;
        if (depth === 0) {
          try {
            const meta = JSON.parse(html.slice(jsonStart, i + 1));
            return Array.isArray(meta.products) ? meta.products : [];
          } catch {
            return [];
          }
        }
      }
    }
    return [];
  }

  /**
   * Storefront search can resolve product IDs (`id:123`) to handles.
   * @param {string[]} ids
   * @returns {Promise<Map<string, string>>}
   */
  async function resolveProductHandles(ids) {
    /** @type {Map<string, string>} */
    const handleById = new Map();
    const pending = [];
    for (const id of ids) {
      if (!id) continue;
      const cached = resolvedHandleCache.get(id);
      if (cached) handleById.set(id, cached);
      else if (!pending.includes(id)) pending.push(id);
    }

    const chunkSize = 10;
    for (let i = 0; i < pending.length; i += chunkSize) {
      const chunk = pending.slice(i, i + chunkSize);
      const query = chunk.map((id) => `id:${id}`).join(' OR ');
      const url = `/search?view=wishlist-product&type=product&q=${encodeURIComponent(query)}`;
      try {
        const response = await fetch(url);
        const text = await response.text();
        const trimmed = text.trim();
        /** @type {Array<{ id?: number | string, handle?: string }>} */
        let products = [];
        if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
          const parsed = JSON.parse(trimmed);
          products = Array.isArray(parsed) ? parsed : parsed.products || [];
        } else {
          products = parseSearchMeta(text);
        }
        products.forEach((product) => {
          const id = extractNumericId(product.id);
          const handle = product.handle ? String(product.handle) : '';
          if (id && handle) {
            resolvedHandleCache.set(id, handle);
            handleById.set(id, handle);
          }
        });
      } catch (error) {
        console.error('[WishlistPage] Failed to resolve product ids:', error);
      }
    }

    return handleById;
  }

  async function init() {

    const wishlistPage = elToHtml(document.querySelector('[data-wishlist-page]'));

    if (!wishlistPage) {
      console.error('[WishlistPage] ❌ Wishlist page not found - element [data-wishlist-page] missing');
      return;
    }

    const contentContainer = elToHtml(document.querySelector('[data-wishlist-products]'));
    const emptyMessage = elToHtml(document.querySelector('[data-wishlist-empty]'));


    if (!contentContainer) {
      console.error('[WishlistPage] ❌ Content container not found - element [data-wishlist-products] missing');
      return;
    }

    const customerId = contentContainer.dataset.customerId;
    const limit = parseInt(contentContainer.dataset.limit || '12', 10);
    let currentPage = parseInt(contentContainer.dataset.page || '1', 10);
    let hasMorePages = true;
    let isLoading = false;
    let usingSavedMetafield = false;


    if (!customerId || customerId === 'null' || customerId === '') {
      console.error('[WishlistPage] ❌ No customer ID found');
      if (emptyMessage) {
        emptyMessage.classList.remove('visually-hidden');
      }
      return;
    }

    const loadingElement = elToHtml(contentContainer.querySelector('.wishlist-page__loading'));

    function showEmptyMessage() {
      if (emptyMessage) {
        emptyMessage.classList.remove('visually-hidden');
      }
      if (contentContainer) {
        const grid = contentContainer.querySelector('.product-grid');
        if (grid) grid.remove();
        const loadingMore = contentContainer.querySelector('.wishlist-page__loading-more');
        if (loadingMore) loadingMore.remove();
      }
    }

    function hideEmptyMessage() {
      if (emptyMessage) {
        emptyMessage.classList.add('visually-hidden');
      }
    }

    async function loadWishlistProducts(page = 1, append = false) {
      if (isLoading || (append && usingSavedMetafield)) {
        return;
      }

      isLoading = true;

      // Show initial loading only on first page
      if (!append && loadingElement) {
        loadingElement.style.display = 'block';
      }

      // Show loading more indicator when appending
      if (append) {
        showLoadingMore();
      }

      const apiUrl = `${WISHLIST_API_BASE}/wishlist/${customerId}?limit=${limit}&page=${page}`;

      try {
        let products = [];
        let totalPages = 1;

        if (!append) {
          const saved = readSavedMetafieldProducts();
          if (saved.length) {
            products = saved;
            usingSavedMetafield = true;
            hasMorePages = false;
          }
        }

        if (!usingSavedMetafield) {
          const response = await fetch(apiUrl);

          if (!response.ok) {
            const errorText = await response.text();
            console.error('[WishlistPage] ❌ HTTP error response:', errorText);
            throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
          }

          const result = await response.json();

          if (result.success && result.data) {
            totalPages = result.data.totalPages || Math.ceil((result.data.total || result.data.totalWishlistsProducts || 0) / limit) || 1;
          } else if (result.totalPages) {
            totalPages = result.totalPages;
          }
          products = extractProductList(result);
          hasMorePages = page < totalPages;
        }

        products = products
          .map((item) => normalizeWishlistItem(item))
          .filter((item) => item !== null);

        if (!products.length) {
          if (!append) {
            showEmptyMessage();
          } else {
            hasMorePages = false;
            hideLoadingMore();
          }
          return;
        }

        const missingIds = products
          .filter((product) => !product.handle && product.productId)
          .map((product) => product.productId);
        if (missingIds.length) {
          const handleById = await resolveProductHandles(missingIds);
          products.forEach((product) => {
            if (!product.handle && handleById.has(product.productId)) {
              product.handle = handleById.get(product.productId) || '';
            }
          });
        }

        if (!append) {
          products.reverse();
        }

        const renderedCount = await renderProducts(products, append);
        if (!append && renderedCount === 0) {
          showEmptyMessage();
          return;
        }
        hideEmptyMessage();
      } catch (error) {
        console.error('[WishlistPage] ❌ Error loading wishlist products:', error);
        console.error('[WishlistPage] Error stack:', error.stack);
        if (!append) {
          showEmptyMessage();
        }
      } finally {
        if (loadingElement) {
          loadingElement.style.display = 'none';
        }
        hideLoadingMore();
        isLoading = false;
      }
    }

    /**
     * @param {string} productHandle
     */
    async function fetchProductData(productHandle) {
      try {
        const response = await fetch(`/products/${productHandle}.js`);
        if (!response.ok) {
          console.error(`[WishlistPage] Failed to fetch product ${productHandle}:`, response.status);
          return null;
        }
        return await response.json();
      } catch (error) {
        console.error(`[WishlistPage] Error fetching product ${productHandle}:`, error);
        return null;
      }
    }

    /**
     * @param {Array<*>} products
     * @param {boolean} [append]
     */
    async function renderProducts(products, append = false) {

      if (!contentContainer) {
        console.error('[WishlistPage] ❌ Content container not available for rendering');
        return 0;
      }

      // Remove loading element (only on first load)
      if (!append) {
        const loading = contentContainer.querySelector('.wishlist-page__loading');
        if (loading) {
          loading.remove();
        }
      }

      // Get or create grid
      /** @type {HTMLElement | null} */
      let gridList = elToHtml(contentContainer.querySelector('.product-grid'));
      if (!gridList) {
        gridList = document.createElement('ul');
        gridList.className = 'product-grid product-grid--wishlist';
        gridList.setAttribute('role', 'list');
        gridList.setAttribute('data-testid', 'product-grid');
        gridList.setAttribute('product-grid-view', 'default');
        // Set CSS variables for grid columns and gap
        gridList.style.setProperty('--mobile-columns', '2');
        gridList.style.setProperty('--product-grid-columns-desktop', 'repeat(4, 1fr)');
        gridList.style.setProperty('--product-grid-gap', '20px');
        gridList.style.setProperty('gap', '20px');
        gridList.style.setProperty('row-gap', '20px');
        gridList.style.setProperty('column-gap', '20px');
        if (gridList instanceof HTMLElement) {
          gridList.style.marginTop = '25px';
        }
        contentContainer.appendChild(gridList);
      } else if (!append) {
        gridList.innerHTML = '';
        // Ensure CSS variables are set
        gridList.style.setProperty('--product-grid-columns-desktop', 'repeat(4, 1fr)');
        gridList.style.setProperty('--product-grid-gap', '20px');
        gridList.style.setProperty('gap', '20px');
        gridList.style.setProperty('row-gap', '20px');
        gridList.style.setProperty('column-gap', '20px');
        if (gridList instanceof HTMLElement) {
          gridList.style.marginTop = '25px';
        }
      }

      // Fetch full product data for each product and render
      const productPromises = products.map(async (product, /** @type {number} */ index) => {

        // Extract product handle
        const handle = product.handle || '';
        if (!handle) {
          console.warn(`[WishlistPage] ⚠️ Product ${index + 1} skipped - no handle`);
          return null;
        }

        // Fetch full product data
        const fullProduct = await fetchProductData(handle);
        
        if (!fullProduct) {
          console.warn(`[WishlistPage] ⚠️ Product ${index + 1} (${handle}) skipped - fetch failed`);
          return null;
        }

        // Extract product ID from GID format
        let productId = String(product.productId ?? product.id ?? fullProduct.id ?? '');
        if (productId.startsWith('gid://shopify/Product/')) {
          productId = productId.replace('gid://shopify/Product/', '');
        }

        const productCard = createProductCard(fullProduct, productId);
        return productCard;
      });

      // Wait for all products to be fetched and rendered
      const productCards = await Promise.all(productPromises);
      const validCards = productCards.filter(card => card !== null);

      validCards.forEach((card) => {
        const listItem = document.createElement('li');
        listItem.className = 'product-grid__item';
        listItem.appendChild(card);
        if (gridList) gridList.appendChild(listItem);
      });

      return validCards.length;
    }

    /**
     * @param {*} product
     * @param {string} productId
     */
    function createProductCard(product, productId) {

      const productUrl = `/products/${product.handle}`;
      const featuredImage = product.featured_image || (product.images && product.images.length > 0 ? product.images[0] : null);
      const imageUrl = featuredImage || '';
      const imageAlt = featuredImage?.alt || product.title || '';

      // Get variant pricing
      const variant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
      const price = variant ? (variant.price / 100) : (product.price ? (product.price / 100) : 0);
      const comparePrice = variant ? (variant.compare_at_price ? (variant.compare_at_price / 100) : null) : null;
      const isAvailable = variant ? variant.available : (product.available !== false);
      const hasVariants = product.variants && product.variants.length > 1 && product.options && product.options.length > 0;
      const isOnSale = comparePrice && comparePrice > price;
      const salePercentOff = isOnSale && comparePrice > 0 ? Math.round((comparePrice - price) / comparePrice * 100) : 0;

      // Check if product uses alternative template
      let tagsArray = [];
      try {
        if (Array.isArray(product.tags)) {
          tagsArray = product.tags;
        } else if (typeof product.tags === 'string') {
          tagsArray = product.tags
            ? product.tags.split(',').map(
                /**
                 * @param {string} t
                 */
                function (t) {
                  return t.trim();
                }
              )
            : [];
        }
      } catch (e) {
        console.warn('[WishlistPage] Error parsing tags:', e);
        tagsArray = [];
      }


      const isAlternativeTemplate = tagsArray.some(
        /**
         * @param {string} tag
         */
        function (tag) {
        const lowerTag = String(tag).toLowerCase();
        return lowerTag === 'alternative-template' || 
               lowerTag === 'alternative_template' ||
               lowerTag === 'alternative template' ||
               lowerTag === 'consultation';
        }
      ) ||
          (product.metafields && product.metafields.custom && product.metafields.custom.use_alternative_template === true) ||
          (product.template_suffix && product.template_suffix.toLowerCase() === 'alternative');

      const isLimitedOffer = tagsArray.some(
        /**
         * @param {string} tag
         */
        (tag) => {
          const normalized = String(tag).toLowerCase().trim().replace(/_/g, '-').replace(/ /g, '-');
          return normalized === 'limited-offer';
        }
      );

      // Format price
      const formattedPrice = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price);

      const formattedComparePrice = comparePrice ? new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(comparePrice) : null;

      // Escape HTML
      /** @type {(text: string) => string} */
      const escapeHtml = (text) => {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      };

      const card = document.createElement('product-card');
      card.className = 'product-card ayudra-product-card' + (isLimitedOffer ? ' ayudra-product-card--no-click' : '');
      card.setAttribute('data-product-id', productId);
      card.setAttribute('data-wishlist-product-id', productId);
      if (isLimitedOffer) {
        card.setAttribute('data-limited-offer', '');
        card.setAttribute('data-no-navigation', '');
      }

      card.innerHTML = `
        <a href="${isLimitedOffer ? '#' : productUrl}" class="product-card__link ayudra-product-card__link" ref="productCardLink" aria-label="${escapeHtml(product.title || '')}"${isLimitedOffer ? ' tabindex="-1" aria-hidden="true"' : ''}>
          <span class="visually-hidden">${escapeHtml(product.title || '')}</span>
        </a>
        <div class="ayudra-product-card__inner">
          <div class="ayudra-product-card__image-wrapper">
            ${isLimitedOffer
              ? `<span class="ayudra-product-card__image-link" role="img" aria-label="${escapeHtml(product.title || '')}">`
              : `<a href="${productUrl}" class="ayudra-product-card__image-link" aria-label="${escapeHtml(product.title || '')}">`}
              ${imageUrl ? `<img 
                src="${imageUrl}" 
                alt="${escapeHtml(imageAlt)}" 
                class="ayudra-product-card__image"
                width="800"
                height="800"
                loading="lazy"
                decoding="async"
                sizes="(min-width: 750px) 25vw, 50vw"
              />` : `<div class="ayudra-product-card__placeholder">
                <svg class="ayudra-product-card__placeholder-svg" viewBox="0 0 525.5 525.5">
                  <path d="M262.8 0C117.8 0 0 117.8 0 262.8s117.8 262.8 262.8 262.8 262.8-117.8 262.8-262.8S407.8 0 262.8 0zm0 485.1c-122.5 0-222.3-99.8-222.3-222.3S140.3 40.5 262.8 40.5 485.1 140.3 485.1 262.8 385.3 485.1 262.8 485.1z"/>
                  <path d="M262.8 125.4c-75.8 0-137.4 61.6-137.4 137.4s61.6 137.4 137.4 137.4 137.4-61.6 137.4-137.4-61.6-137.4-137.4-137.4zm0 234.3c-53.5 0-96.9-43.4-96.9-96.9s43.4-96.9 96.9-96.9 96.9 43.4 96.9 96.9-43.4 96.9-96.9 96.9z"/>
                </svg>
              </div>`}
            ${isLimitedOffer ? '</span>' : '</a>'}
            ${isOnSale && isAvailable && salePercentOff > 0 ? `<span class="ayudra-product-card__badge ayudra-product-card__badge--percent-off">${salePercentOff}% OFF</span>` : ''}
            ${!isAvailable ? '<span class="ayudra-product-card__badge ayudra-product-card__badge--sold-out">Sold Out</span>' : ''}
            <button
              type="button"
              class="wishlist-heart wishlist-heart--card wishlist-heart--active"
              data-wishlist-heart
              data-product-id="${productId}"
              aria-label="Remove from wishlist"
            >
              <svg
                class="wishlist-heart__icon wishlist-heart__icon--empty"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <svg
                class="wishlist-heart__icon wishlist-heart__icon--filled"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                  fill="currentColor"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
          <div class="ayudra-product-card__info">
            <h3 class="ayudra-product-card__title">
              ${isLimitedOffer
                ? `<span class="ayudra-product-card__title-link">${escapeHtml(product.title || '')}</span>`
                : `<a href="${productUrl}" class="ayudra-product-card__title-link">${escapeHtml(product.title || '')}</a>`}
            </h3>
            <div class="ayudra-product-card__price">
              ${isOnSale ? `
                <span class="ayudra-product-card__price--sale">${formattedPrice}</span>
                <span class="ayudra-product-card__price--compare">${formattedComparePrice}</span>
              ` : `
                <span class="ayudra-product-card__price--regular">${formattedPrice}</span>
              `}
            </div>
            ${!isLimitedOffer ? (isAvailable ? `
            <div class="ayudra-product-card__add-to-cart">
              <button 
                type="button" 
                class="ayudra-product-card__button ${isAlternativeTemplate ? 'ayudra-product-card__button--consultation' : ''}"
                data-product-id="${product.id}"
                data-product-handle="${product.handle}"
                data-variant-id="${variant ? variant.id : ''}"
                data-product-title="${escapeHtml(product.title || '')}"
                data-product-url="${productUrl}"
                data-atc-state="default"
                ${isAlternativeTemplate ? 'data-alternative-template="true"' : ''}
                ${hasVariants ? 'data-has-variants="true"' : ''}
              >
                <span class="ayudra-product-card__button-text">${isAlternativeTemplate ? 'Book Consultation' : 'Move to bag'}</span>
                <span class="ayudra-product-card__button-loader" style="display: none;">
                  <svg class="ayudra-product-card__spinner" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="9" cy="9" r="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="50.27" stroke-dashoffset="37.7" opacity="0.3"/>
                    <circle cx="9" cy="9" r="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="50.27" stroke-dashoffset="37.7">
                      <animate attributeName="stroke-dashoffset" values="37.7;12.57;37.7" dur="1s" repeatCount="indefinite"/>
                    </circle>
                  </svg>
                </span>
                <span class="ayudra-product-card__button-checkmark" style="display: none;">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 9L7 13L15 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </button>
            </div>
            ` : `
            <div class="ayudra-product-card__add-to-cart">
              <button type="button" class="ayudra-product-card__button ayudra-product-card__button--disabled" disabled>
                <span>Sold Out</span>
              </button>
            </div>
            `) : ''}
          </div>
        </div>
      `;

      // Mark wishlist heart as active
      const heartButton = card.querySelector('[data-wishlist-heart]');
      if (heartButton) {
        heartButton.classList.add('wishlist-heart--active');
      }

      // Setup add to cart button event listener and state management
      const addToCartButton = card.querySelector('.ayudra-product-card__button[data-product-handle]');
      if (addToCartButton instanceof HTMLButtonElement && !addToCartButton.disabled) {
        // Store productId on button for wishlist removal
        const numericProductId = typeof productId === 'string' && productId.startsWith('gid://shopify/Product/')
          ? productId.replace('gid://shopify/Product/', '')
          : String(productId);
        addToCartButton.setAttribute('data-wishlist-product-id', numericProductId);
        // Function to update button state
        /**
         * @param {'default' | 'loading' | 'success'} state
         */
        const updateButtonState = (state) => {
          const buttonText = addToCartButton.querySelector('.ayudra-product-card__button-text');
          const buttonLoader = addToCartButton.querySelector('.ayudra-product-card__button-loader');
          const buttonCheckmark = addToCartButton.querySelector('.ayudra-product-card__button-checkmark');

          addToCartButton.dataset.atcState = state;
          
          if (state === 'loading') {
            addToCartButton.disabled = true;
            if (buttonText instanceof HTMLElement) buttonText.style.display = 'none';
            if (buttonLoader instanceof HTMLElement) buttonLoader.style.display = 'block';
            if (buttonCheckmark instanceof HTMLElement) buttonCheckmark.style.display = 'none';
          } else if (state === 'success') {
            addToCartButton.disabled = true;
            if (buttonText instanceof HTMLElement) buttonText.style.display = 'none';
            if (buttonLoader instanceof HTMLElement) buttonLoader.style.display = 'none';
            if (buttonCheckmark instanceof HTMLElement) buttonCheckmark.style.display = 'block';
          } else {
            addToCartButton.disabled = false;
            if (buttonText instanceof HTMLElement) buttonText.style.display = 'block';
            if (buttonLoader instanceof HTMLElement) buttonLoader.style.display = 'none';
            if (buttonCheckmark instanceof HTMLElement) buttonCheckmark.style.display = 'none';
          }
        };

        // Use capture phase (true) to ensure this runs BEFORE other handlers
        addToCartButton.addEventListener('click', async function(e) {
          const button = this;
          const productHandle = button.dataset.productHandle;
          const isAlternativeTemplate = button.dataset.alternativeTemplate === 'true';
          const hasVariants = button.dataset.hasVariants === 'true';


          // Check loading state first
          if (button.dataset.atcState === 'loading' || button.disabled) {
            return;
          }

          // Handle alternative template (consultation booking) - open consultation modal FIRST
          if (isAlternativeTemplate) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            

            const productId = button.dataset.productId;
            const variantId = button.dataset.variantId;
            const productTitle = button.dataset.productTitle || '';
            const productUrl = button.dataset.productUrl || '';
            
            // Store product info on button for later removal
            const numericProductId = typeof productId === 'string' && productId.startsWith('gid://shopify/Product/')
              ? productId.replace('gid://shopify/Product/', '')
              : String(productId);
            button.setAttribute('data-wishlist-product-id', numericProductId);
            button.setAttribute('data-wishlist-consultation-modal', 'true');
            
            // Get consultation modal and set product data
            const modal = document.getElementById('consultation-booking-modal');
            if (modal) {
              if (productId) modal.dataset.productId = productId;
              if (variantId) modal.dataset.variantId = variantId;
              if (productTitle) modal.dataset.productTitle = productTitle;
              if (productUrl) modal.dataset.productUrl = productUrl;
              
              // Store wishlist product ID on modal for tracking
              modal.dataset.wishlistProductId = numericProductId;
              
              // Open modal - check if it's a dialog-component
              const modalWithDialog = /** @type {HTMLElement & { showDialog?: () => void }} */ (modal);
              if (typeof modalWithDialog.showDialog === 'function') {
                modalWithDialog.showDialog();
              } else {
                const dialog = modal?.querySelector('dialog');
                if (dialog) {
                  dialog.showModal();
                } else {
                  console.error('[WishlistPage] Consultation booking modal dialog not found');
                }
              }
            } else {
              console.error('[WishlistPage] Consultation booking modal element not found');
            }
            
            // Listen for cart update after consultation booking (when product is added to cart)
            /** @param {Event} event */
            const handleCartUpdateFromConsultation = async (event) => {
              // Check if this was from the consultation modal
              const modal = document.getElementById('consultation-booking-modal');
              const wasFromWishlist = modal && modal.dataset.wishlistProductId && button.dataset.wishlistConsultationModal === 'true';
              
              if (wasFromWishlist) {
                const wishlistProductId = button.dataset.wishlistProductId || numericProductId;
                const win = /** @type {any} */ (window);
                const wishlistManager = win.WishlistManager;
                
                if (wishlistManager && wishlistProductId) {
                  
                  try {
                    await wishlistManager.removeFromWishlist(wishlistProductId);
                    
                    // Remove the product card from the page after a short delay
                    setTimeout(() => {
                      const listItem = button.closest('.product-grid__item');
                      if (listItem instanceof HTMLElement) {
                        listItem.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
                        listItem.style.opacity = '0';
                        listItem.style.transform = 'scale(0.9)';
                        setTimeout(() => {
                          listItem.remove();
                          // Check if wishlist is empty
                          const grid = document.querySelector('.wishlist-page__content .product-grid');
                          if (grid && grid.children.length === 0) {
                            const emptyMessage = document.querySelector('[data-wishlist-empty]');
                            if (emptyMessage) {
                              emptyMessage.classList.remove('visually-hidden');
                            }
                          }
                        }, 300);
                      }
                    }, 500);
                  } catch (wishlistError) {
                    console.error('[WishlistPage] Error removing from wishlist:', wishlistError);
                  }
                }
                
                // Clean up
                button.removeAttribute('data-wishlist-consultation-modal');
                if (modal) {
                  delete modal.dataset.wishlistProductId;
                }
                document.removeEventListener('cart:update', handleCartUpdateFromConsultation);
              }
            };
            
            // Listen for cart update (only once per button click)
            document.addEventListener('cart:update', handleCartUpdateFromConsultation, { once: true });
            
            return;
          }

          // Handle products with variants - open variant selection modal
          if (hasVariants) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            const productId = button.dataset.productId;
            const productHandle = button.dataset.productHandle;
            
            // Store product info on button for later removal
            const numericProductId = typeof productId === 'string' && productId.startsWith('gid://shopify/Product/')
              ? productId.replace('gid://shopify/Product/', '')
              : String(productId);
            button.setAttribute('data-wishlist-product-id', numericProductId);
            button.setAttribute('data-wishlist-variant-modal', 'true');
            
            // Dispatch event to open variant modal
            const openModalEvent = new CustomEvent('openVariantModal', {
              detail: {
                productId: productId,
                productHandle: productHandle
              }
            });
            document.dispatchEvent(openModalEvent);
            
            // Mark modal as being used for wishlist
            const modal = document.getElementById('variant-selection-modal');
            if (modal) {
              modal.dataset.wishlistProductId = numericProductId;
            }
            
            // Listen for cart update after variant is added from modal
            /** @param {Event} event */
            const handleCartUpdateFromModal = async (event) => {
              // Check if modal was just used (check data attribute)
              const modal = document.getElementById('variant-selection-modal');
              const wasFromWishlist = modal && modal.dataset.wishlistProductId && button.dataset.wishlistVariantModal === 'true';
              
              if (wasFromWishlist) {
                const wishlistProductId = button.dataset.wishlistProductId || numericProductId;
                const win = /** @type {any} */ (window);
                const wishlistManager = win.WishlistManager;
                
                if (wishlistManager && wishlistProductId) {
                  
                  try {
                    await wishlistManager.removeFromWishlist(wishlistProductId);
                    
                    // Remove the product card from the page after a short delay
                    setTimeout(() => {
                      const listItem = button.closest('.product-grid__item');
                      if (listItem instanceof HTMLElement) {
                        listItem.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
                        listItem.style.opacity = '0';
                        listItem.style.transform = 'scale(0.9)';
                        setTimeout(() => {
                          listItem.remove();
                          // Check if wishlist is empty
                          const grid = document.querySelector('.wishlist-page__content .product-grid');
                          if (grid && grid.children.length === 0) {
                            const emptyMessage = document.querySelector('[data-wishlist-empty]');
                            if (emptyMessage) {
                              emptyMessage.classList.remove('visually-hidden');
                            }
                          }
                        }, 300);
                      }
                    }, 500);
                  } catch (wishlistError) {
                    console.error('[WishlistPage] Error removing from wishlist:', wishlistError);
                  }
                }
                
                // Clean up
                button.removeAttribute('data-wishlist-variant-modal');
                if (modal) {
                  delete modal.dataset.wishlistProductId;
                }
                document.removeEventListener('cart:update', handleCartUpdateFromModal);
              }
            };
            
            // Listen for cart update (only once per button click)
            document.addEventListener('cart:update', handleCartUpdateFromModal, { once: true });
            
            return;
          }

          // Prevent default for regular add to cart
          e.preventDefault();
          e.stopPropagation();

          // Set loading state
          updateButtonState('loading');

          try {
            // Fetch product to get first available variant
            const productResponse = await fetch(`/products/${productHandle}.js`);
            if (!productResponse.ok) {
              throw new Error('Failed to fetch product');
            }
            /** @type {{ id?: number; variants?: Array<{ available?: boolean; id: number }> }} */
            const productData = await productResponse.json();

            const variant = productData.variants && productData.variants.length > 0 
              ? productData.variants.find((v) => v.available) || productData.variants[0]
              : null;

            if (!variant) {
              throw new Error('No variant available');
            }

            // Get cart drawer section IDs for updating
            const cartItemsComponents = document.querySelectorAll('cart-items-component');
            /** @type {string[]} */
            const sectionIds = [];
            cartItemsComponents.forEach((item) => {
              if (item instanceof HTMLElement && item.dataset.sectionId) {
                sectionIds.push(item.dataset.sectionId);
              }
            });

            // Use FormData format like product-form for proper section updates
            const formData = new FormData();
            formData.append('id', String(variant.id));
            formData.append('quantity', '1');
            if (sectionIds.length > 0) {
              formData.append('sections', sectionIds.join(','));
            }

            // Use Shopify's cart API
            const cartResponse = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Accept': 'application/json',
              },
              body: formData
            });

            const cartData = await cartResponse.json();

            if (cartResponse.ok && !cartData.error) {
              // Success - show checkmark
              updateButtonState('success');
              
              // Dispatch cart update event for other components
              const cartUpdateEvent = new CustomEvent('cart:update', {
                detail: {
                  sourceId: 'wishlist-page',
                  data: {
                    source: 'wishlist-page',
                    itemCount: 1,
                    productId: productData.id,
                    variantId: variant.id.toString(),
                    sections: cartData.sections || {}
                  }
                }
              });
              document.dispatchEvent(cartUpdateEvent);

              // Also trigger cart icon update
              const cartIconEvent = new CustomEvent('cart:updated');
              document.dispatchEvent(cartIconEvent);

              // Remove from wishlist after successful cart addition
              try {
                const win = /** @type {any} */ (window);
                const wishlistManager = win.WishlistManager;
                const wishlistProductId = button.dataset.wishlistProductId || button.dataset.productId;
                if (wishlistManager && wishlistProductId) {
                  // Extract numeric product ID
                  let numericProductId = wishlistProductId;
                  if (typeof wishlistProductId === 'string' && wishlistProductId.startsWith('gid://shopify/Product/')) {
                    numericProductId = wishlistProductId.replace('gid://shopify/Product/', '');
                  }
                  
                  await wishlistManager.removeFromWishlist(numericProductId);
                  
                  // Remove the product card from the page after a short delay
                  setTimeout(() => {
                    const listItem = button.closest('.product-grid__item');
                    if (listItem instanceof HTMLElement) {
                      listItem.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
                      listItem.style.opacity = '0';
                      listItem.style.transform = 'scale(0.9)';
                      setTimeout(() => {
                        listItem.remove();
                        // Check if wishlist is empty
                        const grid = document.querySelector('.wishlist-page__content .product-grid');
                        if (grid && grid.children.length === 0) {
                          const emptyMessage = document.querySelector('[data-wishlist-empty]');
                          if (emptyMessage) {
                            emptyMessage.classList.remove('visually-hidden');
                          }
                        }
                      }, 300);
                    }
                  }, 1000);
                }
              } catch (wishlistError) {
                console.error('[WishlistPage] Error removing from wishlist:', wishlistError);
                // Don't fail the whole operation if wishlist removal fails
              }

              // Reset after 2 seconds (but card will be removed before this)
              setTimeout(() => {
                updateButtonState('default');
              }, 2000);
            } else {
              throw new Error(cartData.description || 'Failed to add to cart');
            }
          } catch (error) {
            console.error('[WishlistPage] Error adding to cart:', error);
            updateButtonState('default');
          }
        });
      }

      return card;
    }

    function showLoadingMore() {
      if (!contentContainer) return;
      
      // Remove existing loading more indicator
      const existing = contentContainer.querySelector('.wishlist-page__loading-more');
      if (existing) existing.remove();

      // Create loading more indicator
      const loadingMore = document.createElement('div');
      loadingMore.className = 'wishlist-page__loading-more';
      loadingMore.innerHTML = '<div class="wishlist-page__loading-more-text">Loading more products...</div>';
      contentContainer.appendChild(loadingMore);
    }

    function hideLoadingMore() {
      if (!contentContainer) return;
      const loadingMore = contentContainer.querySelector('.wishlist-page__loading-more');
      if (loadingMore) loadingMore.remove();
    }

    function setupInfiniteScroll() {
      /** @type {ReturnType<typeof setTimeout> | undefined} */
      let scrollTimeout;
      
      window.addEventListener('scroll', () => {
        // Debounce scroll events
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          // Check if user scrolled near bottom
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          
          // Load more when user is 200px from bottom
          const threshold = 200;
          
          if (scrollTop + windowHeight >= documentHeight - threshold) {
            if (hasMorePages && !isLoading) {
              currentPage++;
              loadWishlistProducts(currentPage, true);
            }
          }
        }, 100);
      });
    }

    // Setup infinite scroll
    setupInfiniteScroll();
    
    // Initial load
    loadWishlistProducts(currentPage);
    
    // Mark as initialized
    globalWin.wishlistPageInitialized = true;
  }

  // Wait for DOM and initialize - try multiple times to ensure DOM is ready
  
  function tryInit() {
    const wishlistPage = elToHtml(document.querySelector('[data-wishlist-page]'));
    
    // Also check URL as fallback
    const isWishlistURL = window.location.pathname.includes('wishlist') || window.location.href.includes('wishlist');
    
    if (wishlistPage) {
      init();
    } else if (isWishlistURL) {
      // URL matches but element not found - condition might not be matching
      console.warn('[WishlistPage] ⚠️ URL suggests wishlist page but element not found!');
      
      // Stop retrying after reasonable attempts
      if (globalWin.wishlistPageRetryCount === undefined) {
        globalWin.wishlistPageRetryCount = 0;
      }
      globalWin.wishlistPageRetryCount++;
      
      if (globalWin.wishlistPageRetryCount < 10) {
        setTimeout(tryInit, 200);
      } else {
        console.error('[WishlistPage] ❌ Element not found after 10 attempts.');
        
        // Fallback: Create the element structure if URL matches but Liquid didn't render it
        const mainContent = document.querySelector('#MainContent') || document.querySelector('main');
        if (mainContent) {
          const customerId = elToHtml(document.querySelector('[data-customer-id]'))?.dataset?.customerId || 
                            document.querySelector('meta[name="customer-id"]')?.getAttribute('content');
          
          if (customerId) {
            const fallbackHTML = `
              <div class="wishlist-page" data-wishlist-page>
                <div class="wishlist-page__header">
                  <h1 class="wishlist-page__title">My Wishlist</h1>
                </div>
                <div class="wishlist-page__content" data-wishlist-products data-customer-id="${customerId}" data-limit="12" data-page="1">
                  <div class="wishlist-page__loading">
                    <p>Loading wishlist...</p>
                  </div>
                </div>
                <div class="wishlist-page__empty visually-hidden" data-wishlist-empty>
                  <a href="/collections" class="wishlist-page__empty-link button">Continue Shopping</a>
                </div>
                <div class="wishlist-page__pagination" data-wishlist-pagination></div>
              </div>
            `;
            
            const sectionWrapper = mainContent.querySelector('.section-content-wrapper') || mainContent;
            if (sectionWrapper) {
              sectionWrapper.insertAdjacentHTML('beforeend', fallbackHTML);
              init();
            } else {
              console.error('[WishlistPage] ❌ Could not find container for fallback structure');
            }
          } else {
            console.error('[WishlistPage] ❌ Customer ID not found for fallback');
          }
        }
      }
    } else {
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(tryInit, 100);
    });
  } else {
    setTimeout(tryInit, 100);
  }
})();
